import React, { createContext, useContext, useEffect, useState } from "react";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";

interface StripeContextType {
    stripe: Stripe | null;
    isLoading: boolean;
}

const StripeContext = createContext<StripeContextType>({
    stripe: null,
    isLoading: true,
});

export const useStripeContext = () => {
    const context = useContext(StripeContext);
    if (!context) {
        throw new Error("useStripeContext must be used within StripeProvider");
    }
    return context;
};

interface StripeProviderProps {
    children: React.ReactNode;
    publishableKey?: string;
}

export const StripeProvider: React.FC<StripeProviderProps> = ({
    children,
    publishableKey,
}) => {
    const [stripe, setStripe] = useState<Stripe | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const initStripe = async () => {
            // Use the provided key or get from environment
            const key =
                publishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

            if (!key) {
                console.error("Stripe publishable key not found");
                setIsLoading(false);
                return;
            }

            try {
                const stripeInstance = await loadStripe(key);
                setStripe(stripeInstance);
            } catch (error) {
                console.error("Failed to load Stripe:", error);
            } finally {
                setIsLoading(false);
            }
        };

        initStripe();
    }, [publishableKey]);

    return (
        <StripeContext.Provider value={{ stripe, isLoading }}>
            {children}
        </StripeContext.Provider>
    );
};

// Wrapper component for Elements that requires publishable key
interface StripeElementsWrapperProps {
    children: React.ReactNode;
    options?: any;
}

export const StripeElementsWrapper: React.FC<StripeElementsWrapperProps> = ({
    children,
    options,
}) => {
    const { stripe } = useStripeContext();

    if (!stripe) {
        return <div>Loading payment system...</div>;
    }

    return (
        <Elements stripe={stripe} options={options}>
            {children}
        </Elements>
    );
};
